import type { InjectionProtocol } from "@src/packages/injection-protocol/types";

/**
 * The administration methods a patient can be offered, in her calendar's words.
 *
 * This table used to be keyed by a protocol's `form` and labelled in the
 * coach's own vocabulary — "Un stylo", "Un flacon". That vocabulary is a
 * procedure's idea of a device and it does not line up with hers: she chose
 * from "Seringue pré-remplie / Stylo injecteur / Flacon + seringue / Poudre +
 * solvant / Multi-flacons" when she added the medication, and two of those
 * collapse into one `form` while a third translates to nothing.
 *
 * So the question is asked in her words now, from the same keys her own form
 * uses, and her answer **is** the value the calendar stores. The two hold one
 * fact instead of two that have to be kept in step — which is the whole of
 * Anaïs's ask, and it removes the translation that produced three separate bugs
 * on its own.
 *
 * The order is this file's, not the registry's, so the question reads the same
 * way whichever protocol happened to be added first.
 */
export const ADMINISTRATION_METHODS = [
  "pre_filled_pen",
  "pre_filled_syringe",
  "injection_solution",
  "lyophilized_powder",
  "multi_vial",
] as const;

export type AdministrationMethod = (typeof ADMINISTRATION_METHODS)[number];

/**
 * The label for a method, from the buttons she originally chose from.
 *
 * `add_medication.delivery_method_option.*` and nothing of the coach's own, so
 * a card that reads her answer back and a button that offers it cannot word it
 * differently.
 */
export function administrationMethodLabelKey(method: string): string {
  return `add_medication.delivery_method_option.${method}`;
}

/**
 * Her own words for a stored device, or nothing.
 *
 * The API's `form` enum has fourteen values and five of them are administration
 * methods her own form can write; the rest — `other` above all, which is what
 * "Multi-flacons" is stored as — have no button and therefore no label. A
 * translation lookup on those returns the key path itself, so an unguarded call
 * puts `add_medication.delivery_method_option.other` on screen in front of a
 * patient. Both surfaces read the device name through here.
 */
export function recordedDeviceLabel(
  method: string | null | undefined,
  t: (key: string) => string,
): string {
  const stored = method?.trim().toLowerCase();
  if (!stored) return "";
  const label = t(administrationMethodLabelKey(stored));
  return label.includes("add_medication.") ? "" : label;
}

/**
 * The devices a medication is dispensed as, as a sentence fragment.
 *
 * In her own form's words, joined the way her language joins a list. Named
 * rather than counted: "we have steps for some of them" tells her nothing she
 * can act on, where "Seringue pré-remplie" is something she can hold her box up
 * against. The refusal card reads this, on both surfaces.
 */
export function deviceList(
  methods: readonly string[],
  t: (key: string) => string,
): string {
  const names = methods
    .map((method) => recordedDeviceLabel(method, t))
    .filter(Boolean);
  if (names.length <= 1) return names[0] ?? "";
  const last = names[names.length - 1]!;
  return `${names.slice(0, -1).join(", ")} ${t("care.injection_coach.and")} ${last}`;
}

/**
 * The methods these procedures can actually be dispensed as.
 *
 * Union of what each candidate declares, deduplicated, in this file's order.
 * Only what the medication is sold as: offering "Multi-flacons" for a pen is
 * how a patient confirms a device she cannot be holding, and the whole point of
 * asking is that her answer means something afterwards.
 */
export function methodsFor(
  protocols: readonly InjectionProtocol[],
): readonly AdministrationMethod[] {
  const offered = new Set(
    protocols.flatMap((protocol) => protocol.prescriptionForms),
  );
  return ADMINISTRATION_METHODS.filter((method) => offered.has(method));
}

/**
 * The procedures that answer to the method she has, or all of them if it says
 * nothing.
 *
 * Narrowing on her own recorded value rather than on a translation of it. An
 * unrecognised or absent method narrows nothing, which leaves the caller to ask
 * — never to guess.
 */
export function protocolsForMethod(
  protocols: readonly InjectionProtocol[],
  method: string | null | undefined,
): readonly InjectionProtocol[] {
  const normalized = method?.trim().toLowerCase();
  if (!normalized) return protocols;
  const matching = protocols.filter((protocol) =>
    protocol.prescriptionForms.includes(normalized),
  );
  return matching.length > 0 ? matching : protocols;
}

/**
 * Whether her record names a method this medication is actually dispensed as.
 *
 * The question behind the confirmation card: there is something on file, and it
 * is one of the answers that means a procedure here. "Flacon + seringue" on a
 * follitropin alfa is a real answer to a different question, and she is asked
 * rather than walked through a reconstitution she has nothing to reconstitute.
 */
export function methodIsOffered(
  protocols: readonly InjectionProtocol[],
  method: string | null | undefined,
): boolean {
  const normalized = method?.trim().toLowerCase();
  if (!normalized) return false;
  return protocols.some((protocol) =>
    protocol.prescriptionForms.includes(normalized),
  );
}
